/**
 * 批量订单导入页面 - 管理员上传 Excel 批量开通 VIP 和充值 Token
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { useAuthStore } from '@/stores/auth';
import { batchOrderApi, type PreviewRow, type PreviewStats } from '@/services/batchOrderApi';
import { Upload, FileSpreadsheet, Loader2, Download, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

type PageState = 'upload' | 'preview' | 'processing' | 'done';

export function BatchOrderPage() {
  const { accessToken, user } = useAuthStore();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<PageState>('upload');
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [stats, setStats] = useState<PreviewStats | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [resultStats, setResultStats] = useState({ success: 0, skip: 0, fail: 0 });

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      navigate('/admin');
    }
  }, [user, navigate]);

  const handleFileSelect = useCallback(async (file: File) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('请上传 Excel 文件（.xlsx 或 .xls）');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('文件大小不能超过 5MB');
      return;
    }

    setFileName(file.name);
    setUploading(true);

    try {
      const result = await batchOrderApi.preview(file, accessToken!);
      setPreviewRows(result.rows);
      setStats(result.stats);
      setState('preview');
      toast.success(`解析成功，共 ${result.stats.total} 条记录`);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : '上传失败';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  }, [accessToken]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleExecute = useCallback(async () => {
    setShowConfirm(false);
    setState('processing');

    try {
      const result = await batchOrderApi.execute(previewRows, accessToken!);
      setResultBlob(result.blob);
      setResultStats({
        success: result.successCount,
        skip: result.skipCount,
        fail: result.failCount,
      });
      setState('done');
      toast.success('批量处理完成');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : '处理失败';
      toast.error(msg);
      setState('preview');
    }
  }, [previewRows, accessToken]);

  const handleDownload = useCallback(() => {
    if (!resultBlob) return;
    const url = URL.createObjectURL(resultBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `批量订单处理结果_${new Date().toLocaleDateString('zh-CN').replace(/\//g, '-')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [resultBlob]);

  const handleReset = useCallback(() => {
    setState('upload');
    setPreviewRows([]);
    setStats(null);
    setResultBlob(null);
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  return (
    <LayoutWithSidebar>
      <div className="flex-1 overflow-auto">
        <div className="container mx-auto p-6 max-w-6xl">
          <div className="mb-6">
            <h1 className="text-2xl font-bold">批量订单导入</h1>
            <p className="text-muted-foreground mt-1">
              上传 Excel 订单文件，批量开通 VIP 会员并充值 Token
            </p>
          </div>

          {/* 上传区域 */}
          {state === 'upload' && (
            <div
              className="border-2 border-dashed rounded-lg p-12 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-12 w-12 text-primary animate-spin" />
                  <p className="text-lg font-medium">正在解析文件...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <Upload className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <p className="text-lg font-medium">拖拽 Excel 文件到此处，或点击上传</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      支持 .xlsx / .xls 格式，最大 5MB
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 预览 / 处理中 / 完成 */}
          {state !== 'upload' && (
            <>
              {/* 文件信息 + 统计 */}
              <div className="flex items-center gap-3 mb-4">
                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                <span className="font-medium">{fileName}</span>
                {state === 'preview' && (
                  <Button variant="outline" size="sm" onClick={handleReset}>
                    重新上传
                  </Button>
                )}
              </div>

              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                  <StatCard label="总数" value={stats.total} />
                  <StatCard label="已注册" value={stats.registered} variant="success" />
                  <StatCard label="未注册" value={stats.unregistered} variant="warning" />
                  <StatCard label="3年VIP+20M" value={stats.threeYear} variant="info" />
                  <StatCard label="2年VIP+5M" value={stats.twoYear} variant="info" />
                </div>
              )}

              {/* 处理中进度 */}
              {state === 'processing' && (
                <div className="mb-6 p-6 border rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="font-medium">正在批量处理中，请勿关闭页面...</span>
                  </div>
                  <Progress className="h-2" />
                </div>
              )}

              {/* 完成结果 */}
              {state === 'done' && (
                <div className="mb-6 p-6 border rounded-lg bg-muted/30">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    处理完成
                  </h3>
                  <div className="flex gap-6 mb-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>成功: {resultStats.success}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      <span>跳过: {resultStats.skip}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-600" />
                      <span>失败: {resultStats.fail}</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={handleDownload}>
                      <Download className="h-4 w-4 mr-2" />
                      下载结果 Excel
                    </Button>
                    <Button variant="outline" onClick={handleReset}>
                      继续导入
                    </Button>
                  </div>
                </div>
              )}

              {/* 数据表格 */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>收件人</TableHead>
                      <TableHead>邮箱（账号）</TableHead>
                      <TableHead>VIP 类型</TableHead>
                      <TableHead>Token 额度</TableHead>
                      <TableHead>注册状态</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.map((row, idx) => (
                      <TableRow key={row.email} className={!row.registered ? 'opacity-50' : ''}>
                        <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell className="font-mono text-sm">{row.email}</TableCell>
                        <TableCell>
                          <Badge variant={row.vipType === '3year' ? 'default' : 'secondary'}>
                            {row.vipLabel}
                          </Badge>
                        </TableCell>
                        <TableCell>{(row.tokenQuota / 1_000_000).toFixed(0)}M</TableCell>
                        <TableCell>
                          {row.registered ? (
                            <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                              已注册 ({row.username})
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-yellow-600 border-yellow-200 bg-yellow-50">
                              未注册
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* 操作按钮 */}
              {state === 'preview' && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    将处理 {stats?.registered ?? 0} 个已注册用户，跳过 {stats?.unregistered ?? 0} 个未注册用户
                  </p>
                  <Button
                    size="lg"
                    disabled={!stats?.registered}
                    onClick={() => setShowConfirm(true)}
                  >
                    确认执行
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 确认弹窗 */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认批量执行</AlertDialogTitle>
            <AlertDialogDescription>
              即将为 {stats?.registered ?? 0} 个用户开通 VIP 会员并充值 Token。
              此操作不可撤销，确定要继续吗？
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleExecute}>
              确认执行
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </LayoutWithSidebar>
  );
}

function StatCard({ label, value, variant }: {
  label: string;
  value: number;
  variant?: 'success' | 'warning' | 'info';
}) {
  const colorMap = {
    success: 'text-green-600',
    warning: 'text-yellow-600',
    info: 'text-blue-600',
  };
  const color = variant ? colorMap[variant] : '';

  return (
    <div className="border rounded-lg p-3 text-center">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
